import { defineStore } from "pinia";
import { computed, ref, shallowRef } from "vue";
import { transactionLogApi } from "./transactionLogApi";
import type {
  RegistryEventSummary,
  RegistryEventsQuery,
} from "./transactionLogTypes";

export const useTransactionLogStore = defineStore("transactionLog", () => {
  const events = shallowRef<RegistryEventSummary[]>([]);
  const nextCursor = ref<string | null>(null);
  const hasLinkedEvents = ref(false);
  const isLoading = ref(false);
  const isLoadingMore = ref(false);
  const lastPageFailed = ref(false);
  let currentQuery: RegistryEventsQuery | null = null;
  let requestSequence = 0;

  // NOTE: a failed page stops automatic loading until the next first page; an infinite scroll would
  // otherwise retry it on every scroll check.
  const canLoadMore = computed(
    () =>
      hasLinkedEvents.value &&
      nextCursor.value !== null &&
      !isLoading.value &&
      !isLoadingMore.value &&
      !lastPageFailed.value
  );

  const fetchFirstPage = async (query: RegistryEventsQuery) => {
    const request = ++requestSequence;
    isLoading.value = true;
    isLoadingMore.value = false;
    lastPageFailed.value = false;
    try {
      const { data } = await transactionLogApi.listEvents(query);
      // NOTE: filters can change while a page is in flight; only the latest request may land.
      if (request !== requestSequence) return;
      currentQuery = query;
      events.value = data.events;
      nextCursor.value = data.next_cursor;
      hasLinkedEvents.value = data.has_linked_events;
    } finally {
      if (request === requestSequence) isLoading.value = false;
    }
  };

  const fetchNextPage = async () => {
    if (
      !currentQuery ||
      !nextCursor.value ||
      isLoading.value ||
      isLoadingMore.value
    )
      return;
    const request = requestSequence;
    isLoadingMore.value = true;
    try {
      const { data } = await transactionLogApi.listEvents(
        currentQuery,
        nextCursor.value
      );
      if (request !== requestSequence) return;
      events.value = [...events.value, ...data.events];
      nextCursor.value = data.next_cursor;
    } catch (error) {
      if (request === requestSequence) lastPageFailed.value = true;
      throw error;
    } finally {
      if (request === requestSequence) isLoadingMore.value = false;
    }
  };

  const reset = () => {
    requestSequence++;
    currentQuery = null;
    events.value = [];
    nextCursor.value = null;
    hasLinkedEvents.value = false;
    isLoading.value = false;
    isLoadingMore.value = false;
    lastPageFailed.value = false;
  };

  return {
    events,
    nextCursor,
    hasLinkedEvents,
    isLoading,
    isLoadingMore,
    canLoadMore,
    fetchFirstPage,
    fetchNextPage,
    reset,
  };
});
